<template>
    <p>{{ $t('guide.guide_popover_step_3_desc') }}</p>

    <div :class="['pt-tooltip-wrap', readOnly && isHoverButton && 'hover']">
        <span class="tooltip" v-if="disabledTip">
            <i>{{ disabledTip }}</i>
        </span>
        <span
            ref="tooltipButton"
            style="
                 {
                    display: inline-block;
                }
            "
        >
            <pt-button
                color="lightblue"
                :type="props.step.done ? 'outline' : 'contain'"
                :disabled="readOnly"
                @click="openConversionDialog"
            >
                {{ $t('guide.guide_popover_step_3_btn_create') }}
            </pt-button>
        </span>
    </div>
</template>

<script lang="ts" setup>
import { computed, onMounted, onUnmounted, ref } from 'vue';
import useEventBus from '@assets/plugins/eventBus';
import { useProfileInfo, ProfileSettingMenu, Product } from '@models/profile/useCurrentProfile';
import type { Profile } from '@models/profile/profileList';
import { useGuideStore, GuideStep } from '@master/store/guide.store';
import { useRouter, useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { usePtiUsage } from '@models/profile/usePtiUsage';
import useShowAuthDialog from '@master/hooks/service/useShowAuthDialog';

interface Props {
    step: GuideStep;
}

const props = defineProps<Props>();

const emit = defineEmits(['hide', 'show', 'trackEvent']);
const { eventBus } = useEventBus();
const { t } = useI18n();
const { profileInfo, setProfileProductMenu, ptiProduct } = useProfileInfo();
const { ptiModuleUsage } = usePtiUsage();
const { updateGuideState } = useGuideStore();
const router = useRouter();
const route = useRoute();
const isHoverButton = ref(false);
const tooltipButton = ref();
const { checkAuth } = useShowAuthDialog();

const readOnly = computed(() => {
    return profileInfo.value?.ptiUserRole
        ? !['owner', 'admin'].includes(profileInfo.value?.ptiUserRole)
        : false;
});

const disabledTip = computed(() => {
    return readOnly.value
        ? t('guide.no_set_conversion_access_tips', {
              owner: profileInfo.value?.ownerEmail
          })
        : '';
});

onMounted(() => {
    tooltipButton.value?.addEventListener('mouseenter', handleMouseEnter, false);
    tooltipButton.value?.addEventListener('mouseleave', handleMouseLeave, false);
});

onUnmounted(() => {
    tooltipButton.value?.removeEventListener('mouseenter', handleMouseEnter, false);
    tooltipButton.value?.removeEventListener('mouseleave', handleMouseLeave, false);
});

function openConversionDialog() {
    emit('hide');
    eventBus.$emit('hideVerifyDialog');
    eventBus.$emit('hideSetup');

    checkAuth(ptiProduct.value as Product, profileInfo.value as Profile, () => {
        emit('trackEvent', 'guide_cv_create_click', {
            has_completed: ptiModuleUsage.value?.['conversion'] ? 1 : 0
        });

        // 如果用户已经设置转化,跳转到cv setting 页面
        if (ptiModuleUsage.value?.['conversion']) {
            const item = ProfileSettingMenu['insight'].find(item => {
                return item['key'] === 'CONVERSION';
            });
            setProfileProductMenu();
            if (route.name === 'Insight') {
                eventBus.$emit('insightRouteChange', item);
            } else {
                router.push({ path: `/${route.params.sid}/insight/setting/conversion` });
                eventBus.$emit('changeProfileMenuItem', item);
            }
        } else {
            if (router.currentRoute.value?.path?.includes('/insight/conversion/onboarding')) {
                eventBus.$emit('showConversionDialog');
            } else {
                eventBus.$emit('goToConversionAndShowDialog');
                updateGuideState('conversionDialogVisible', true);
            }
        }
    });
}

function handleMouseEnter() {
    isHoverButton.value = true;
}

function handleMouseLeave() {
    isHoverButton.value = false;
}
</script>

<style lang="scss" module>
@import '@style/import.scss';
</style>
